// ─────────────────────────────────────────────────────────────────────────────
// OTA update readout — TEMPORARY INSTRUMENT. See the removal checklist at the
// bottom of this file.
//
// ⚠️ WHY THIS EXISTS: getLatest() answers with DATA — the update server's
// decision, before anything is applied — but there is no console on the device
// to read it in. Safari Web Inspector is the only debugger for an iOS WKWebView
// and it needs a Mac; there isn't one. chrome://inspect is not a fallback either,
// because webContentsDebuggingEnabled is false in a store build and must stay
// false (it hard-fails both store uploads and deploy.yml's gate). So the reading
// is rendered INTO the app, on the one staff surface that is already reachable
// on device: /admin/ota. Same reasoning as HeaderDebugReadout.jsx.
//
// ⚠️⚠️ THIS IS THE INSTRUMENT, NOT THE ANSWER — AND THE TWO GET CONFLATED.
//
// ⚠️ RETRACTED: this block used to claim the production/staging pair
// "characterises the VERSION-PIN blocker" — production pinned to 1.0.2-ci.185,
// ranking below native 1.0.2. That claim was wrong twice over and both halves
// are corrected below rather than deleted, because the reasoning is what keeps
// getting repeated:
//
//   1. The pair cannot characterise anything per-channel. getLatest's channel
//      argument only sets `defaultChannel` — see the CORRECTION block below.
//   2. The pin state it asserted is UNCHANGED — and my retraction of it was the
//      wrong call, recorded here because it is the same mistake twice. The
//      device answered 1.0.3-ci.228/229 and I read that as "production has moved
//      off ci.185". It had not. A full-width `channel list` capture on
//      2026-09-09 shows production STILL linked to 1.0.2-ci.185 and staging on
//      1.0.3-ci.229 — the device was on STAGING, which is exactly what this
//      panel not capturing getChannel() alongside getLatest() made unknowable.
//      The original version-pin finding stands: production's 1.0.2-ci.185 ranks
//      below native 1.0.2 under the under-native guard, so every fresh 1.0.2
//      install on production has received nothing since 28 August.
//
//      THE LESSON IS THE PAIRING, NOT THE NUMBER. A bundle version read without
//      the channel that served it cannot distinguish "production moved" from
//      "this device is not on production". Read the channel record for channel
//      state; use this panel for what THIS DEVICE is being served.
//
// It does NOT answer the floorless-bundle question. What `disable_auto_update:
// metadata` does when the channel's linked bundle carries `min_update_version:
// null` is a SEPARATE characterisation — the one the staging rehearsal exists
// for (see the ROLLBACK block in .github/workflows/ota-publish.yml) — and it
// still needs the staging flip and steps 1-6 run in order. This panel is what
// makes those steps runnable without a debugger; it is not a substitute for
// running them. Do not read the pair as the rehearsal's result.
//
// ⚠️ READ, DON'T APPLY. getLatest() asks the server what it WOULD serve and
// applies nothing. reset() is the exception and says so on the button.
//
// ⚠️⚠️ CORRECTION — getLatest({ channel }) IS NOT A CHANNEL SELECTOR, AND THIS
// PANEL ORIGINALLY CLAIMED IT WAS. Read on device, the two channel buttons
// returned BYTE-IDENTICAL payloads. The plugin source at the pinned version
// (8.51.14) says why, and it is not a bug:
//
//   CapacitorUpdaterPlugin.swift:2853  getLatest reads call.getString("channel")
//   CapgoUpdater.swift:1091-1094       parameters = createInfoObject(); then
//                                      parameters.defaultChannel = channel
//   CapgoUpdater.swift:1032-1033       createInfoObject() hardcodes channel: nil
//   InternalUtils.swift:134-135        InfoObject has BOTH `channel` and
//                                      `defaultChannel` as separate wire fields
//
// So the argument is written into `defaultChannel` — the FALLBACK the backend
// uses only when the device has no channel of its own — and the `channel` field
// is sent as nil on every getLatest request. A device that already has a
// resolved channel (a persisted setChannel, or a Dashboard/Public-API Device
// Override) ignores the argument entirely, which is exactly the identical-payload
// result. The parameter is accepted and effectively dropped.
//
// WHAT THAT MEANS FOR THIS PANEL: a single getLatest() reading describes THE
// DEVICE'S OWN CHANNEL and nothing else. It cannot compare two channels, and it
// cannot on its own attribute a result to production or to staging — pair every
// reading with getChannel() to know which channel answered. To read a different
// channel you must actually MOVE the device (the two buttons at the top of this
// page, or a Device Override from the Capgo dashboard).
//
// ⚠️ EVERY FIELD BELOW IS RENDERED RAW. The panel does not summarise, classify
// or pretty-print a verdict — it prints what the plugin returned. A readout that
// interprets is a readout that can be wrong in a way the screenshot cannot show.
// `kind` ('up_to_date' | 'blocked' | 'failed', @since 8.45.11) is the backend's
// own classification; the plugin's d.ts is explicit that it, not a parse of
// `error`, is what to read.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { COMMIT_SHA } from '../constants/build';

// ⚠️ A THROW IS A READING, NOT A BLANK PANEL. getLatest() is documented to throw
// on a failed update check or a transport failure, and a swallowed rejection
// looks exactly like "the server said nothing" — which is a different finding.
// Everything the bridge puts on a rejected native call is kept: Capacitor
// surfaces the native message on `.message`, and some paths carry `.code` /
// `.errorMessage` instead.
function asThrown(e) {
  return {
    __threw: true,
    name: e?.name ?? null,
    message: e?.message ?? String(e),
    code: e?.code ?? null,
    errorMessage: e?.errorMessage ?? null,
  };
}

export default function OtaUpdateReadout() {
  const native = Capacitor.isNativePlatform();
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copyNote, setCopyNote] = useState('');

  function push(label, payload) {
    setEntries((prev) => [...prev, { label, at: new Date().toISOString(), payload }]);
  }

  async function record(label, fn) {
    setBusy(true);
    setCopyNote('');
    try {
      push(label, await fn());
    } catch (e) {
      push(label, asThrown(e));
    } finally {
      setBusy(false);
    }
  }

  // ⚠️ EVERY READING CARRIES THE CHANNEL THAT ANSWERED IT. Because the argument
  // only sets defaultChannel (see the correction at the top of this file), the
  // response alone cannot say which channel produced it — getChannel() is what
  // makes the reading attributable instead of merely suggestive. Recording them
  // together is the whole reason a second identical payload was legible as a
  // finding rather than dismissed as a repeat.
  const readChannel = (channel) =>
    record(`getLatest({ channel: '${channel}' })  [sets defaultChannel only]`, async () => {
      const [latest, resolved] = await Promise.all([
        CapacitorUpdater.getLatest({ channel }),
        CapacitorUpdater.getChannel().catch(asThrown),
      ]);
      return { requested_defaultChannel: channel, resolved_channel: resolved, latest };
    });

  async function readBoth() {
    // Sequential, not Promise.all: two concurrent update checks against the same
    // endpoint is not the request pattern being characterised, and a shared
    // failure would be impossible to attribute.
    //
    // Kept as a pair DESPITE the argument being a fallback: two identical
    // payloads is the positive evidence that the device has a resolved channel
    // overriding it. Reading once would leave that indistinguishable from a
    // working selector.
    await readChannel('production');
    await readChannel('staging');
  }

  // ⚠️ THIS ONE LINE SETTLES A QUESTION NOTHING ELSE HERE CAN. isPluginAvailable
  // asks the NATIVE BRIDGE whether THIS BUILD registered the plugin — so it
  // reports on the shipped binary regardless of which web bundle is loaded over
  // it, which means a staging bundle answers it for the App Store 1.0.2 binary.
  // The alternative routes are a rebuild of the release commit (inference about
  // the binary, not the binary) or the as-built Podfile.lock artifact (expired).
  //
  // 'Badge' is the plugin's registered name, checked against the package rather
  // than assumed: it calls registerPlugin('Badge') and BadgePlugin.swift
  // declares jsName = "Badge".
  //
  // ✅ ANSWERED ON DEVICE, 2026-09-09: Badge === false on the shipped 1.0.2
  // binary. The plugin is NOT in it. See the note in hooks/useAppBadge.js, which
  // is where the cap-sync reasoning that predicted otherwise lives.
  //
  // If this reads false, NOTHING can clear an app-icon badge on this build, on
  // any device — @capacitor-firebase/messaging is in the binary but exposes no
  // badge get/set API (only presentationOptions, which governs whether a
  // FOREGROUND notification may apply the badge from its own payload). That is
  // a shipped defect waiting on the next store release, not an OTA fix.
  function readPlugins() {
    return record('isPluginAvailable', async () => ({
      Badge: Capacitor.isPluginAvailable('Badge'),
      FirebaseMessaging: Capacitor.isPluginAvailable('FirebaseMessaging'),
      CapacitorUpdater: Capacitor.isPluginAvailable('CapacitorUpdater'),
      platform: Capacitor.getPlatform(),
    }));
  }

  // Step 4's reading. current() gives the running bundle AND the native version
  // the server is being answered with — the two halves of every rank comparison
  // in this investigation. getBuiltinVersion() and getChannel() ride along
  // because they are free here and unobtainable anywhere else on the device.
  function readDeviceState() {
    return record('current() + getBuiltinVersion() + getChannel()', async () => {
      const [current, builtin, channel] = await Promise.all([
        CapacitorUpdater.current().catch(asThrown),
        CapacitorUpdater.getBuiltinVersion().catch(asThrown),
        CapacitorUpdater.getChannel().catch(asThrown),
      ]);
      return { current, builtin, channel };
    });
  }

  // ── DELAY CONTROLS ─────────────────────────────────────────────────────────
  // ⚠️ THIS SUPPRESSES THE APPLY, NOT THE CHECK, AND THAT DISTINCTION IS THE
  // WHOLE REASON IT WORKS. Nothing in this binary can stop the update check or
  // the download — autoUpdate:true is baked into capacitor.config.ts and
  // isAutoUpdateEnabled() has no setter. But both delay gates sit AFTER the
  // download, on the apply:
  //
  //   CapacitorUpdaterPlugin.swift:4519-4531  installNext(): early return
  //   CapacitorUpdaterPlugin.swift:4430-4437  direct-update path: same gate
  //
  // So the bundle downloads and sits there unapplied, and the device stays on
  // whatever it is on. That is what makes the reset survivable: reset() reloads,
  // auto-update re-pulls, and without this the device is back on the newest
  // bundle within ~30 seconds.
  //
  // ⚠️ ORDER MATTERS: SET THE DELAY *BEFORE* reset(), NEVER AFTER. There is no
  // after — reset() destroys the JS context on its way to the reload, so nothing
  // scheduled behind it runs. The delay is persisted in UserDefaults
  // (DELAY_CONDITION_PREFERENCES), so it is already in force when the reloaded
  // context comes up.
  //
  // ⚠️ 'date' IS THE ONLY KIND THAT SURVIVES WHAT WE DO TO THIS DEVICE.
  // checkCancelDelay clears 'kill' conditions on app kill (plugin :460) and
  // 'background' ones on background/foreground (:4570, :4673). A 'date'
  // condition is cleared only when the date passes, so it holds across the
  // reset's reload and across a force-quit. parseDateCondition takes ISO8601
  // with or without fractional seconds — toISOString() is exactly right.
  const [delayUntil, setDelayUntil] = useState(null);

  function armDelay(minutes) {
    const until = new Date(Date.now() + minutes * 60000).toISOString();
    return record(`setMultiDelay({ date: ${until} })`, async () => {
      await CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: 'date', value: until }] });
      setDelayUntil(until);
      return { armed: true, kind: 'date', value: until, note: 'Downloads still happen; the APPLY is blocked until this passes.' };
    });
  }

  function clearDelay() {
    return record('cancelDelay()', async () => {
      await CapacitorUpdater.cancelDelay();
      setDelayUntil(null);
      return { armed: false, note: 'Normal auto-update behaviour restored. A pending downloaded bundle may apply on the next launch.' };
    });
  }

  // ⚠️ THE ONE BUTTON THAT CHANGES THE DEVICE. reset() reverts to the builtin
  // bundle and triggers an immediate reload; the plugin's own d.ts says the
  // promise "may never resolve because the app will be reloaded". So the
  // invocation is recorded BEFORE the call — if the panel is still on screen
  // afterwards, the reset did not fire, and that is itself the reading.
  async function doReset() {
    const ok = window.confirm(
      'reset() reverts THIS DEVICE to the builtin bundle (the web layer baked into the App Store binary) and reloads the app immediately.\n\n' +
      'The JavaScript context is destroyed, so everything captured in this panel is lost — copy it first.\n\n' +
      (delayUntil
        ? `A delay is armed until ${delayUntil}, so the re-pulled bundle should download but NOT apply.\n\nContinue?`
        : 'NO DELAY IS ARMED. Auto-update will re-pull and re-apply within about 30 seconds, undoing this. ' +
          'Arm "setMultiDelay date +1h" first unless you specifically want that.\n\nContinue anyway?')
    );
    if (!ok) return;
    push('reset({ toLastSuccessful: false }) — invoked', {
      note: 'The app reloads immediately and this call may never resolve. If you can still read this line, the reset did NOT fire — record that.',
    });
    try {
      await CapacitorUpdater.reset({ toLastSuccessful: false });
      push('reset() — returned', {
        returned: true,
        note: 'Returned without reloading. Unexpected — record it.',
      });
    } catch (e) {
      push('reset() — threw', asThrown(e));
    }
  }

  // One document, so a copy is a complete transcript rather than whichever
  // reading happened to be on screen. The build stamp is here for the same
  // reason REACT_APP_COMMIT_SHA exists: a reading nobody can pin to a bundle has
  // already been the mechanism behind three false all-clears.
  const transcript = JSON.stringify(
    { build: COMMIT_SHA, platform: Capacitor.getPlatform(), captured_at: new Date().toISOString(), readings: entries },
    null,
    2
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(transcript);
      setCopyNote('Copied.');
    } catch (e) {
      // Clipboard API needs a secure context and can be refused outright in a
      // WKWebView. Not a dead end: the block below is selectable by design, so
      // say so instead of failing silently.
      setCopyNote(`Clipboard refused (${e?.message || e}) — long-press the JSON below to select it, or screenshot it.`);
    }
  }

  return (
    <div style={{ marginTop: 24, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: 6 }}>
        Update-check readout (temporary)
      </div>

      {/* ⚠️ THIS PARAGRAPH IS THE POINT OF THE PANEL, NOT PREAMBLE. Without it
          the next reader takes the production/staging pair as the rehearsal's
          answer, which is the conflation this panel was built to prevent. */}
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#991b1b', lineHeight: 1.65, margin: '0 0 12px' }}>
        <strong>The channel argument does not select a channel.</strong> Verified on device (identical payloads
        from both buttons) and in the 8.51.14 source: <code>getLatest</code> writes it into the request&rsquo;s{' '}
        <code>defaultChannel</code> field — the fallback used only when the device has no channel of its own —
        while the separate <code>channel</code> field is sent as <code>nil</code> every time. A device with a
        resolved channel ignores it. <strong>So one reading describes this device&rsquo;s own channel and
        nothing else</strong>; always pair it with <code>getChannel()</code> to know which channel answered, and
        to read a different one, actually move the device (the buttons above, or a Capgo Device Override).
      </div>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#78350f', lineHeight: 1.65, margin: '0 0 12px' }}>
        <strong>This is the instrument, not the answer.</strong> Whatever a reading here shows about which
        bundle is served, it does <strong>not</strong> answer what <code>disable_auto_update: metadata</code>{' '}
        does when the linked bundle carries <code>min_update_version: null</code>. That is the separate
        floorless-bundle question, and it still needs the staging flip and steps 1–6 run in order. These
        buttons are what make those steps runnable without a debugger.
      </div>

      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, margin: '0 0 12px' }}>
        <code>getLatest()</code> asks the server what it <em>would</em> serve and applies nothing, so both
        channels can be read from this device without switching it. Results are appended in order — a throw is
        recorded as a reading, never swallowed.
      </p>

      {/* ⚠️ THE ORDER IS THE METHOD. Both obvious sequences fail: flipping the
          channel to metadata first makes every subsequent upload hard-fail
          without a floor, and publishing first lets the device auto-apply the
          new bundle so there is no candidate left to evaluate. The delay is what
          breaks that deadlock — it holds the device behind while the channel and
          the bundle are moved around it. */}
      <ol style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.7, margin: '0 0 12px', paddingLeft: 20 }}>
        <li><strong>setMultiDelay date +1h</strong> — device downloads but stops applying</li>
        <li>push a trivial change → staging publishes <code>ci.N+1</code></li>
        <li><strong>current()</strong> — confirm the device is STILL on the old bundle. This is the check that the delay held; do not skip it</li>
        <li>flip staging to <code>metadata</code> (the linked bundle has no floor — that is the state under test)</li>
        <li><strong>getLatest</strong> with <code>getChannel()</code> paired — <code>kind</code> answers it</li>
        <li><strong>cancelDelay()</strong>, and flip staging back to <code>none</code></li>
      </ol>

      <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, margin: '0 0 12px' }}>
        <code>isPluginAvailable</code> is a separate question again, and the only place it can be answered:
        it asks the native bridge what <strong>this binary</strong> registered, so it reports on the App Store
        build no matter which web bundle is loaded over it. If <code>Badge</code> reads <code>false</code>,
        no device on this build can clear an app-icon badge and that waits on the next store release.
      </p>

      {!native && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#9a3412', marginBottom: 12 }}>
          Not the native app — the updater plugin is a web stub here and every call returns a placeholder
          (<code>0.0.0</code>, &ldquo;Cannot getLatest current bundle in web&rdquo;). Run this on the device.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={readBoth} disabled={!native || busy} style={btn('#1d4ed8', !native || busy)}>
          Read both (proves the fallback)
        </button>
        <button onClick={() => readChannel('production')} disabled={!native || busy} style={btn('#166534', !native || busy)}>
          getLatest defaultChannel=production
        </button>
        <button onClick={() => readChannel('staging')} disabled={!native || busy} style={btn('#b45309', !native || busy)}>
          getLatest defaultChannel=staging
        </button>
        <button onClick={readDeviceState} disabled={!native || busy} style={btn('#374151', !native || busy)}>
          current()
        </button>
        <button onClick={readPlugins} disabled={!native || busy} style={btn('#6d28d9', !native || busy)}>
          isPluginAvailable
        </button>
        <button onClick={doReset} disabled={!native || busy} style={btn('#b91c1c', !native || busy)}>
          reset() → builtin (reloads)
        </button>
      </div>

      {/* ⚠️ THE BANNER IS NOT DECORATION. A forgotten date condition is a phone
          that silently stops applying updates — no error, no symptom, it just
          quietly stays behind, which is the same invisible-failure shape as the
          OTA version pin. It renders whenever a delay is armed, states the exact
          expiry, and carries its own cancel button so clearing it never requires
          finding the right control further down the page.

          ⚠️ IT ONLY KNOWS ABOUT DELAYS ARMED IN THIS SESSION. The plugin exposes
          no getter for the stored conditions at 8.51.14 — setMultiDelay and
          cancelDelay are write-only — so a delay armed before a reload cannot be
          read back and this banner will be ABSENT while one is still in force.
          Treat "no banner" as "unknown", not as "none": if in doubt, tap Cancel
          delay anyway. It is safe on a device with no delay set. */}
      {delayUntil && (
        <div style={{ background: '#7c2d12', color: '#fff', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.6, marginBottom: 12 }}>
          <strong>⏸ Delay ACTIVE until {delayUntil}</strong>
          <div style={{ marginTop: 4, opacity: 0.9 }}>
            This device downloads updates but will not apply them until then. Clear it when the reading is done —
            a forgotten delay is a phone that silently stops updating.
          </div>
          <button onClick={clearDelay} disabled={busy} style={{ ...btn('#fff', busy), color: '#7c2d12', marginTop: 8 }}>
            Cancel delay now
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => armDelay(60)} disabled={!native || busy} style={btn('#7c2d12', !native || busy)}>
          setMultiDelay date +1h
        </button>
        <button onClick={() => armDelay(15)} disabled={!native || busy} style={btn('#9a3412', !native || busy)}>
          +15m
        </button>
        <button onClick={clearDelay} disabled={!native || busy} style={btn('#166534', !native || busy)}>
          cancelDelay()
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <button onClick={copy} disabled={!entries.length} style={btn('#111827', !entries.length)}>
          Copy transcript
        </button>
        <button onClick={() => { setEntries([]); setCopyNote(''); }} disabled={!entries.length} style={btn('#6b7280', !entries.length)}>
          Clear
        </button>
        {copyNote && <span style={{ fontSize: 12, color: '#6b7280' }}>{copyNote}</span>}
      </div>

      <pre
        style={{
          margin: 0,
          padding: '12px 14px',
          background: '#0b1020',
          color: '#e5e7eb',
          borderRadius: 8,
          fontSize: 11.5,
          lineHeight: 1.5,
          font: '11.5px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace',
          maxHeight: 360,
          overflow: 'auto',
          // Selectable ON PURPOSE — when the clipboard is refused, long-press
          // selection is the only remaining way off the device short of a
          // screenshot. -webkit-touch-callout must stay default for iOS to offer
          // the selection handles at all.
          userSelect: 'text',
          WebkitUserSelect: 'text',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {entries.length ? transcript : 'No readings yet.'}
      </pre>
    </div>
  );
}

function btn(bg, disabled) {
  return {
    background: disabled ? '#e5e7eb' : bg,
    color: disabled ? '#9ca3af' : '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '9px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVAL CHECKLIST (this is scaffolding, not a feature):
//   1. delete this file
//   2. AdminOta.jsx — the import and the <OtaUpdateReadout /> render line
//   3. nothing else: this component owns no state outside itself, adds no route,
//      and touches no OTA behaviour. reset() is the only call that changes the
//      device, and it is behind a confirm.
//   4. remove it once the floorless-bundle rehearsal is characterised and the
//      1.0.3-ci.N promotion has shipped — NOT when the version-pin pair is read.
//      Those are the two separate findings this panel keeps apart.
// ─────────────────────────────────────────────────────────────────────────────
